<template>
  <MainLayout>
    <div v-if="loader && !campaign && isEditPage" class="text-center">
      <Loader
        :size="'big'"
      />
    </div>
    <div v-if="isCreatePage">
      <CreateCampaign />
    </div>
    <div class="mt-3" v-if="!isEditPage || (isEditPage && campaign)">
      <ProgrammaticCampaignEdit
        :baseCampaign="campaign"
        v-if="isProgrammaticCampaign"
      />
      <NativeCampaignEdit
        :baseCampaign="campaign"
        v-if="isNativeCampaign"
      />
      <SearchCampaignEdit
        :baseCampaign="campaign"
        v-if="isGoogleAdsCampaign"
      />
      <YoutubeCampaignEdit
        :baseCampaign="campaign"
        v-if="isYoutubeCampaign"
      />
      <MetaCampaignEdit
        :baseCampaign="campaign"
        v-if="isMetaCampaign"
      />
      <PushCampaignEdit
        :baseCampaign="campaign"
        v-if="isPushCampaign"
      />

      <SpotifyCampaignEdit
        :baseCampaign="campaign"
        v-if="isSpotifyCampaign"
      />

      <NativeContextualCampaignEdit
        :baseCampaign="campaign"
        v-if="isNativeContextualCampaign"
      />
      <NativeOpenCampaignEdit
        :baseCampaign="campaign"
        v-if="isNativeOpenCampaign"
      />
    </div>
  </MainLayout>
</template>

<script>

import CampaignsService from '@/modules/campaigns/services/campaigns-service'
import variables from '@/mixins/variables'

import CreateCampaign from '@/components/campaigns/CreateCampaign'
import MainLayout from '@/components/layouts/MainLayout'
import MetaCampaignEdit from '@/modules/campaigns/views/meta/MetaCampaignEdit'
import NativeCampaignEdit from '@/modules/campaigns/views/native/NativeCampaignEdit'
import NativeContextualCampaignEdit from '@/modules/campaigns/views/native-contextual/NativeContextualCampaignEdit'
import NativeOpenCampaignEdit from '@/modules/campaigns/views/native-open/NativeOpenCampaignEdit'
import ProgrammaticCampaignEdit from '@/modules/campaigns/views/programmatic/ProgrammaticCampaignEdit'
import PushCampaignEdit from '@/modules/campaigns/views/push/PushCampaignEdit'
import SearchCampaignEdit from '@/modules/campaigns/views/search/SearchCampaignEdit'
import SpotifyCampaignEdit from '@/modules/campaigns/views/spotify/SpotifyCampaignEdit'
import YoutubeCampaignEdit from '@/modules/campaigns/views/youtube/YoutubeCampaignEdit'

import Loader from '@/components/common/Loader/Loader'

import { mapState } from 'vuex'

export default {
  mixins: [variables],
  components: {
    Loader,
    CreateCampaign,
    MainLayout,
    MetaCampaignEdit,
    NativeCampaignEdit,
    NativeContextualCampaignEdit,
    NativeOpenCampaignEdit,
    ProgrammaticCampaignEdit,
    PushCampaignEdit,
    SearchCampaignEdit,
    SpotifyCampaignEdit,
    YoutubeCampaignEdit
  },
  data () {
    return {
      campaign: null,
      loader: false
    }
  },
  metaInfo () {
    return {
      title: this.pageTitle
    }
  },
  created () {
    if (this.isEditPage) {
      this.getCampaign()
    }
  },
  computed: {
    ...mapState('users', {
      user: state => state.user
    }),
    /**
     * Check if is edit page
     */
    isEditPage () {
      return this.$route.name === 'campaigns.edit'
    },
    /**
     * Check if is create page
     */
    isCreatePage () {
      return this.$route.name === 'campaigns.create'
    },
    /**
     * Check if is google ads campaign
     */
    isGoogleAdsCampaign () {
      // If is creation page, get name of route
      if (!this.isEditPage) return this.routeName === 'campaigns.search.create'

      // Check if has campaign and is of correct type
      return this.campaign && this.campaign.type === 'SEARCH'
    },
    /**
     * Check if is a Meta campaign
     */
    isMetaCampaign () {
      // If is creation page, get name of route
      if (!this.isEditPage) return this.routeName === 'campaigns.meta.create'

      // Check if has campaign and is of correct type
      return this.campaign && this.campaign.platform === 'META'
    },
    isNativeCampaign () {
      // If is creation page, get name of route
      if (!this.isEditPage) return this.routeName === 'campaigns.native.create'

      // Check if has campaign and is of correct type
      return this.campaign && this.campaign.type === 'NATIVE' && this.campaign.platform !== 'META' && this.campaign.deal_partner !== 'MGID'
    },
    /**
     * Check if is a Native Contextual campaign
     */
    isNativeContextualCampaign () {
      // If is creation page, get name of route
      if (!this.isEditPage) return this.routeName === 'campaigns.native-contextual.create'

      // Check if has campaign and is of correct type
      return this.campaign && this.campaign.type === 'NATIVE_CONTEXTUAL'
    },
    /**
     * Check if is a Native Contextual campaign
     */
    isNativeOpenCampaign () {
      // If is creation page, get name of route
      if (!this.isEditPage) return this.routeName === 'campaigns.native-open.create'

      // Check if has campaign and is of correct type
      return this.campaign && this.campaign.type === 'NATIVE_OPEN'
    },
    /**
     * Check if is programmatic campaign
     */
    isProgrammaticCampaign () {
      // If is creation page, get name of route
      if (!this.isEditPage) return this.routeName === 'campaigns.programmatic.create'

      // Check if has campaign and is of correct type
      return this.campaign && this.campaign.type === 'PROGRAMMATIC'
    },
    /**
     * Check if is programmatic campaign
     */
    isYoutubeCampaign () {
      // If is creation page, get name of route
      if (!this.isEditPage) return this.routeName === 'campaigns.youtube.create'

      // Check if has campaign and is of correct type
      return this.campaign && this.campaign.type === 'YOUTUBE'
    },
    /**
     * Check if is Spotify campaign
     */
    isSpotifyCampaign () {
      // If is creation page, get name of route
      if (!this.isEditPage) return this.routeName === 'campaigns.spotify.create'

      // Check if has campaign and is of correct type
      return this.campaign && this.campaign.type === 'AUDIO'
    },
    /*
     * Check if is push campaign
     */
    isPushCampaign () {
      // If is creation page, get name of route
      if (!this.isEditPage) return this.routeName === 'campaigns.push.create'

      // Check if has campaign and is of correct type
      return this.campaign && this.campaign.type === 'PUSH'
    },
    /**
     * Get page title
     */
    pageTitle () {
      const array = ['campaigns.programmatic.create', 'campaigns.native.create', 'campaigns.push.create', 'campaigns.youtube.create', 'campaigns.create']
      return array.includes(this.$route.name) ? this.$t('create_campaign') : this.$t('edit_campaign')
    },
    /**
     * Router name
     */
    routeName () {
      return this.$route.name
    }
  },
  methods: {
    /**
     * Get advertiser
     */
    async getCampaign () {
      this.loader = true
      try {
        const campaign = await CampaignsService.getCampaign(this.$route.params.id)
        if (campaign.status === 'FINISHED') return this.$router.push({ name: 'campaigns.show', params: { id: campaign.id } })
        this.campaign = campaign
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
        this.$router.push('/')
      }
      this.loader = false
    }
  }
}
</script>
